import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, SearchCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLES = {
  Yeni: "bg-blue-50 text-blue-700 border-blue-200",
  "İncelemede": "bg-amber-50 text-amber-700 border-amber-200",
  "Tamamlandı": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Arşiv": "bg-slate-100 text-slate-500 border-slate-200",
};

export default function StatusCheck() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const check = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) {
      setError("Lütfen referans kodunuzu girin (örn. KBL-2CF487).");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/submissions/status/${encodeURIComponent(code.trim().toUpperCase())}`);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="durum" className="py-16 sm:py-24" data-testid="status-check-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mx-auto text-center"
        >
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#0000FF] font-semibold">Başvuru Takibi</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A] mt-4">
            Başvurunuzun Durumunu Sorgulayın
          </h2>
          <p className="text-base leading-relaxed text-[#475569] mt-4">
            Formu gönderirken aldığınız referans kodunu girin, başvurunuzun güncel durumunu anında görün.
          </p>

          <form onSubmit={check} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError("");
              }}
              placeholder="Örn. KBL-2CF487"
              className="flex-1 rounded-full border border-[#E2E8F0] bg-white px-6 py-3.5 text-sm font-mono uppercase text-[#0F172A] placeholder:normal-case placeholder:text-[#94A3B8] outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10"
              data-testid="status-code-input"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="status-check-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] text-white font-semibold px-7 py-3.5 transition-colors hover:bg-[#0000FF] disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <SearchCheck size={18} />}
              Sorgula
            </button>
          </form>

          {error && (
            <p className="mt-4 text-sm font-medium text-red-500" data-testid="status-check-error">{error}</p>
          )}

          {result && (
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 bg-white rounded-2xl border border-[#E2E8F0] p-6 sm:p-8 text-left shadow-xl shadow-[#0F172A]/5"
              data-testid="status-result-card"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-sm font-semibold text-[#0000FF]" data-testid="status-result-code">{result.reference_code}</p>
                  <p className="font-display text-lg font-bold text-[#0F172A] mt-1">{result.company_name}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Başvuru: {new Date(result.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <span
                  className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${STATUS_STYLES[result.status] || ""}`}
                  data-testid="status-result-badge"
                >
                  {result.status}
                </span>
              </div>
              <p className="text-sm text-[#475569] mt-4 border-t border-[#F1F5F9] pt-4">
                Sorularınız için mesai saatleri içinde <strong>0212-9120262</strong> veya <strong>0850 811 2 444</strong> numaralı hatlarımızdan bize ulaşabilirsiniz.
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
